import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { getUser, setSession } from "@/lib/api";

/**
 * MagicLink page (25/02/2026)
 *
 * O backend envia links do tipo:
 *   {APP}/auth/magic?t=<jwt>&r=<redirect_path>
 *
 * Este componente:
 *   1. Extrai ``t`` e ``r`` da querystring.
 *   2. Grava o token via ``setSession`` (mesma chave que o Login normal).
 *   3. Verifica que o token é válido chamando ``GET /auth/me``.
 *   4. Redireciona para o path indicado por ``r``.
 *   5. Se falhar, mostra toast e envia para ``/`` (login manual).
 */
export default function MagicLink() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      const token = sp.get("t");
      const redirect = sp.get("r") || "/dashboard";
      if (!token) {
        toast.error("Link inválido — em falta o token de autenticação.");
        nav("/", { replace: true });
        return;
      }
      const previous = getUser();
      try {
        // Grava o token com uma sessão parcial; será substituída pelo
        // GET /auth/me a seguir. ``setSession`` usa as chaves
        // ``caring_token`` + ``caring_user`` que o resto da app espera.
        setSession(token, previous || {});
        const me = await api.get("/auth/me");
        if (me?.data?.id) {
          setSession(token, me.data);
          if (previous && previous.id && previous.id !== me.data.id) {
            toast.info(`Sessão trocada para ${me.data.name || me.data.email}`);
          } else {
            toast.success(`Bem-vindo, ${me.data.name || me.data.email}`);
          }
          nav(redirect, { replace: true });
          return;
        }
        throw new Error("Resposta inválida do servidor");
      } catch (e) {
        localStorage.removeItem("caring_token");
        localStorage.removeItem("caring_user");
        const status = e?.response?.status;
        if (status === 401) {
          toast.error("Link expirado. Faz login manualmente para continuar.");
        } else {
          toast.error(e?.response?.data?.detail || "Não foi possível autenticar. Tenta o login manual.");
        }
        try { sessionStorage.setItem("caring:pendingRedirect", redirect); } catch { /* noop */ }
        nav("/", { replace: true });
      }
    })();
  }, [sp, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40" data-testid="magic-link-loading">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">A autenticar…</p>
      </div>
    </div>
  );
}
